import { Alert, Button, Chip, Collapse, Divider, FormControlLabel, IconButton, InputAdornment, Radio, RadioGroup, Stack, Switch, TextField, Typography, styled } from '@mui/material'
import { CloseRounded as CloseRoundedIcon } from '@mui/icons-material'
import { CartStack } from '../../Cart/CartContent/CartStack'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import type Immutable from 'immutable'
import type UserCartStore from '../../UserData/_stores/UserCartStore'
import SummaryLineLoader from './SummaryLineLoader'
import { useTranslation } from 'react-i18next'
import DineroFactory, { type Dinero } from 'dinero.js'
import { useAppDispatch } from '../../Common/_hooks/useAppDispatch'
import { ApplyPromoCode } from '../../UserData/_actions/UserCartActions'
import SelectRewardDialog from './SelectRewardDialog'
import UserRewardCoupon from '../../Account/GoalsAndRewards/UserRewardCoupon'
import { useAppSelector } from '../../Common/_hooks/useAppSelector'
import useIsSale from '../../Common/_hooks/useIsSale'
import type { ProductItem } from '../../UserData/_stores/UserCartStore'

// section338: Product and fabric-option matches based on the stored HS codes.
const SECTION338_TARIFF_PRODUCT_IDS = [
  35, 36, 38, 41, 47, 60, 62, 66, 71,
  99, 121, 122, 123, 124, 134, 137, 138,

  // section338: Imported blank shirts, polos, and hoodies are excluded because they are not products of Canada.
  // 83, 84, 85, 88, 93, 96, 100, 101, 102, 103, 104, 105, 106, 107, 108, 109, 110, 113, 114, 115, 116, 126, 127,
]
const SECTION338_FABRIC_OPTION_IDS = [1573, 1574, 1575]
// section338: Restore the previous 26.5% tariff only for products present in the pre-Section 338 tariff lists.
const PREVIOUS_TARIFF_PRODUCT_IDS = [
  83, 84, 85, 93, 101, 102, 103, 105, 106, 107, 108,
  88, 96, 100, 104, 109, 110, 111, 112, 113, 114, 115, 116, 128,
]
// 10% Bangladesh tariff + 6.8% duty + $0.187/kg rounded to 0.3% = 17.1%.
const SNAPBACK_TARIFF_RATE = 0.171
const SNAPBACK_TARIFF_PRODUCT_IDS = [139]
// Estimate using the snapback's existing 10% surcharge + 7.5% cotton duty (6505.00.2060).
const DAD_CAP_TARIFF_RATE = 0.175
const DAD_CAP_TARIFF_PRODUCT_IDS = [140]

const PriceLine = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  color: '#999',

  '&.total': {
    fontSize: '18px',
    fontWeight: '500',
    color: '#000'
  },

  '&.cart-total': {
    color: '#000',
    fontWeight: '500',
  },

  '&.discount': {
    color: theme.palette.accent.main,
    fontWeight: '500'
  }
}))

const PriceLabel = styled('span')({

})

const PriceEntry = styled(SummaryLineLoader)({

})

export interface Props {
  carts: Immutable.OrderedMap<string, UserCartStore>
  disabled?: boolean
}

export default function CheckoutSummaryContent(props: Props) {
  const dispatch = useAppDispatch()
  const [t] = useTranslation('cart')

  const cartLoader = useAppSelector(state => state.get('UIData').get('loaders').get('carts'))
  const userRewards = useAppSelector(state => state.get('userData').get('rewards'))
  const rewards = useAppSelector(state => state.get('appData').get('rewards'))
  const loggedIn = useAppSelector(state => state.get('userData').get('loggedIn'))

  const [rewardDialogOpened, setRewardDialogOpened] = useState(false)
  const [promocodeValue, setPromocodeValue] = useState('')

  const [promocode, userReward] = useMemo(() => {
    let tmpPromocode = ''
    let tmpRewardId: number | null = null

    const arrayCarts = props.carts.toIndexedSeq()
    for (const cart of arrayCarts) {
      const cartPromocode = cart.get('promocode')
      if (cartPromocode) {
        tmpPromocode = cartPromocode

        const cartReward = cart.get('id_reward')
        if (cartReward !== null) {
          tmpRewardId = cartReward
        }
      }
    }

    return [
      tmpPromocode,
      tmpRewardId !== null ? userRewards.get(String(tmpRewardId)) : undefined
    ]
  }, [props.carts, userRewards])

  const reward = useMemo(() => {
    return userReward ? rewards.get(String(userReward.get('id_reward'))) : undefined
  }, [userReward, rewards])

  useEffect(() => {
    if (promocode) {
      setDiscountVisible(true)

      if (reward) {
        setDiscountType('reward')
      } else {
        setDiscountType('code')
      }
    }

    setPromocodeValue('')
  }, [promocode, reward])

  const [discountVisible, setDiscountVisible] = useState(!!promocode)
  const [discountType, setDiscountType] = useState<'code' | 'reward'>(reward ? 'reward' : 'code')

  const grandTotal = useMemo(() => {
    let grandTotal: Dinero | undefined
    for (const [, cart] of props.carts) {
      const price = {
        amount: cart.get('total').get('amount'),
        currency: cart.get('total').get('currency')
      }
      grandTotal = grandTotal ? grandTotal.add(DineroFactory(price)) : DineroFactory(price)
    }
    return grandTotal
  }, [props.carts])

  const firstCart = useMemo(() => {
    return props.carts.first()
  }, [props.carts])

  const cartIds = useMemo(() => {
    return props.carts.valueSeq().map(cart => cart.get('id')).toArray()
  }, [props.carts])

  const hasMultipleCarts = props.carts.count() > 1

  const onRewardDialogOpen = useCallback(() => {
    setRewardDialogOpened(true)
  }, [])

  const onRewardDialogClose = useCallback(() => {
    setRewardDialogOpened(false)
  }, [])

  const onSubmitPromocode = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    dispatch(ApplyPromoCode(cartIds, promocodeValue))
  }, [cartIds, promocodeValue])

  const onDeletePromocode = useCallback(() => {
    dispatch(ApplyPromoCode(cartIds, ''))
  }, [cartIds])

  const onChangeDiscountVisible = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDiscountVisible(e.target.checked)
  }, [])

  const onChangeDiscountType = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDiscountType(e.target.value as 'reward' | 'code')
  }, [])

  const onChangePromocodeValue = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPromocodeValue(e.target.value)
  }, [])

  const tariffAmount = useMemo(() => {
    if(!firstCart) return DineroFactory({ amount: 0, currency: 'USD' });

    let amount = DineroFactory({ amount: 0, currency: firstCart.get('subtotal').get('currency') });
    props.carts.forEach(cart => {
      if(cart.get('country') !== 'United States') return;

      cart.get('items').forEach(item => {
        if(item.get('type') !== 'product') return;

        const productItem = item as ProductItem
        const productId = productItem.get('product_id')
        const hasSection338FabricOption = productId === 50
          && productItem.get('options').some(option => SECTION338_FABRIC_OPTION_IDS.includes(option.get('id')))

        // section338: Previous 26.5% tariff conditions retained for rollback.
        // if([83, 84, 85, 93, 101, 102, 103, 105, 106, 107, 108].includes(productId)) {
        //   amount = amount.add(item.get('price').toDinero().multiply(item.get('quantity')).multiply(0.265))
        // } else if([88,96,100,104,109,110,111,112,113,114,115,116, 128].includes(productId)) {
        //   amount = amount.add(item.get('price').toDinero().multiply(item.get('quantity')).multiply(0.265))
        // }

        if(SNAPBACK_TARIFF_PRODUCT_IDS.includes(productId)) {
          amount = amount.add(item.get('price').toDinero().multiply(item.get('quantity')).multiply(SNAPBACK_TARIFF_RATE))
        } else if(DAD_CAP_TARIFF_PRODUCT_IDS.includes(productId)) {
          amount = amount.add(item.get('price').toDinero().multiply(item.get('quantity')).multiply(DAD_CAP_TARIFF_RATE))
        } else if(SECTION338_TARIFF_PRODUCT_IDS.includes(productId) || hasSection338FabricOption) {
          amount = amount.add(item.get('price').toDinero().multiply(item.get('quantity')).multiply(0.5))
        } else if(PREVIOUS_TARIFF_PRODUCT_IDS.includes(productId)) {
          amount = amount.add(item.get('price').toDinero().multiply(item.get('quantity')).multiply(0.265))
        } else if([125,130,135,136].includes(productId)) {
          amount = amount.add(item.get('price').toDinero().multiply(item.get('quantity')).multiply(0.5))
        }
      })
    })

    return amount
  }, [props.carts, firstCart])

  const isSale = useIsSale();

  if (!firstCart) return null

  return <CartStack spacing={2}>
    <Typography variant="h3">{t('Summary')}</Typography>

    {props.carts.valueSeq().map(cart => {
      return <React.Fragment key={cart.get('id')}>
        <Stack spacing={1}>
          {hasMultipleCarts ? <Typography variant="h4">{cart.get('cart_name')}</Typography> : null}
          <PriceLine>
            <PriceLabel>{t('Subtotal')}</PriceLabel>
            <PriceEntry>{cart.get('subtotal').toDinero().add(cart.get('discount_wholesale').toDinero()).toFormat()}</PriceEntry>
          </PriceLine>

          {!cart.get('discount_wholesale').toDinero().isZero() ? <PriceLine className="discount">
            <PriceLabel>{t('Wholesale discount')}</PriceLabel>
            <PriceEntry>{cart.get('discount_wholesale').toDinero().multiply(-1).toFormat()}</PriceEntry>
          </PriceLine> : null}

          <PriceLine>
            <PriceLabel>{t('Shipping')}</PriceLabel>
            <PriceEntry>{cart.get('shipping').toDinero().toFormat()}</PriceEntry>
          </PriceLine>

          { !tariffAmount.isZero() 
            ? <Alert severity="info">{t('Includes {{amount}} tariff', { amount: tariffAmount.toFormat() })}</Alert> : null }

          {!cart.get('discount').toDinero().isZero() ? <>
            { promocode || !isSale ? <PriceLine className="discount">
                <PriceLabel>{t('Discount')}</PriceLabel>
                <PriceEntry>{cart.get('discount').toDinero().multiply(-1).toFormat()}</PriceEntry>
              </PriceLine> : <div>
                <Stack
                  spacing={1}
                  sx={{
                    border: '2px solid',
                    borderColor: 'accent.main',
                    //background: 'black',
                    borderRadius: '10px',
                    p: 1.5,
                    mx: -1,
                    my: 1,
                  }}
                >
                  <Typography variant="h2" sx={{fontSize: '30px',}}>{t('SALE')}</Typography>
                  { /*<Typography variant="h2" sx={{fontSize: '30px'}}>{t('BOXING DAY 2025')}</Typography> */ }
                  <PriceLine className="discount">
                    <PriceLabel>{t('Discount')}</PriceLabel>
                    <PriceEntry>{cart.get('discount').toDinero().multiply(-1).toFormat()}</PriceEntry>
                  </PriceLine>
                </Stack>
              </div> }
            </> : null }

          <PriceLine>
            <PriceLabel>{t('Taxes')}</PriceLabel>
            <PriceEntry>{cart.get('taxes').toDinero().toFormat()}</PriceEntry>
          </PriceLine>

          {hasMultipleCarts ? <PriceLine className="cart-total">
            <PriceLabel>{t('Total')}</PriceLabel>
            <PriceEntry>{cart.get('total').toDinero().toFormat()}</PriceEntry>
          </PriceLine> : null}
        </Stack>
        <Divider />
      </React.Fragment>
    })}

    <PriceLine className="total">
      <PriceLabel>{hasMultipleCarts ? t('Grand total') : t('Total')}</PriceLabel>
      <PriceEntry width={110} height={17}>{grandTotal?.toFormat()}</PriceEntry>
    </PriceLine>

    {['cart', 'ordered'].includes(firstCart.get('status')) && firstCart.get('order_from') !== 'invoice' ? <>
      <Divider />

      <div>
        <FormControlLabel
          control={<Switch
            color="accent"
            checked={discountVisible}
            onChange={onChangeDiscountVisible}
          />}
          label={t('Have a discount?')}
          labelPlacement="start"
          disabled={!!cartLoader || props.disabled}
          componentsProps={{
            typography: {
              variant: 'h3'
            }
          }}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginLeft: 0
          }}
        />
        <Collapse in={discountVisible}>
          <RadioGroup
            value={discountType}
            onChange={onChangeDiscountType}
          >
            <FormControlLabel
              value="code"
              label={<div>
                <Typography variant="body1" component="p">{t('Apply code')}</Typography>
              </div>}
              control={<Radio />}
              disabled={!!cartLoader || props.disabled}
            />

            <Collapse in={discountType === 'code'}>
              <Stack
                spacing={1}
                alignItems="flex-start"
              >
                { !promocode ? <form onSubmit={onSubmitPromocode}>
                  <TextField
                    value={promocodeValue}
                    onChange={onChangePromocodeValue}
                    disabled={!!cartLoader || props.disabled}
                    size="small"
                    sx={{
                      '& .MuiInputBase-root': {
                        alignItems: 'stretch',
                      },
                    }}
                    InputProps={{
                      endAdornment: <InputAdornment
                        position="end"
                        sx={{
                          alignSelf: 'stretch',
                          height: 'auto',
                          maxHeight: 'none',
                          m: 0,
                        }}
                      >
                        <Button
                          variant="outlined"
                          color="secondary"
                          size="medium"
                          type="submit"
                          disabled={!!cartLoader || props.disabled}
                          sx={{
                            alignSelf: 'stretch',
                            backgroundColor: '#fff',
                            borderRadius: '30px',
                            height: 'auto',
                            minHeight: 0,
                          }}
                        >{t('Apply')}</Button>
                      </InputAdornment>
                    }}
                  />
                </form> : null }

                { promocode ? <Chip
                  color="primary"
                  label={promocode}
                  disabled={!!cartLoader || props.disabled}
                  onDelete={onDeletePromocode}
                /> : null }
              </Stack>
            </Collapse>

            {userRewards.count() > 0 && loggedIn ? <>
              <FormControlLabel
                value="reward"
                label={<Stack spacing={1}>
                  <Typography variant="body1" component="p">{t('Apply reward')}</Typography>
                </Stack>}
                control={<Radio />}
                disabled={!!cartLoader || props.disabled}
                style={{
                  alignItems: 'flex-start'
                }}
              />

              <Collapse in={discountType === 'reward'}>
                { reward ? <div style={{
                  position: 'relative',
                  marginTop: '12px',
                  marginRight: '12px'
                }}>
                  <UserRewardCoupon
                    reward={reward}
                    fullWidth
                    small
                  />
                  <div style={{
                    border: '1px solid black',
                    background: '#fff',
                    position: 'absolute',
                    top: '-12px',
                    right: '-12px',
                    borderRadius: '50%'
                  }}>
                    <IconButton
                      color="secondary"
                      size="small"
                      onClick={onDeletePromocode}
                    >
                      <CloseRoundedIcon fontSize="inherit"/>
                    </IconButton>
                  </div>
                </div> : <Button
                  variant="outlined"
                  color="secondary"
                  size="medium"
                  onClick={onRewardDialogOpen}
                  disabled={!!cartLoader || props.disabled}
                >{t('Choose reward')}</Button> }
              </Collapse>
            </> : null}
          </RadioGroup>
        </Collapse>
      </div>
    </> : null}

    { loggedIn ? <SelectRewardDialog
      cartIds={cartIds}
      opened={rewardDialogOpened}
      onClose={onRewardDialogClose}
    /> : null }
  </CartStack>
}
